import { ColorVariable } from './types';

/**
 * Default color variables extracted from your index.css
 * These define the complete color system of your application
 */
export const DEFAULT_COLOR_VARIABLES: ColorVariable[] = [
  // Main colors
  {
    name: '--primary',
    displayName: 'Primary',
    category: 'main',
    description: 'Main brand color used for buttons, links, and highlights',
    defaultLight: '222.2 47.4% 11.2%',
    defaultDark: '210 40% 98%'
  },
  {
    name: '--primary-foreground',
    displayName: 'Primary Text',
    category: 'main',
    description: 'Text color that appears on primary backgrounds',
    defaultLight: '210 40% 98%',
    defaultDark: '222.2 47.4% 11.2%'
  },
  
  // Secondary colors
  {
    name: '--secondary',
    displayName: 'Secondary',
    category: 'secondary',
    description: 'Secondary color for subtle backgrounds and buttons',
    defaultLight: '210 40% 96.1%',
    defaultDark: '222.2 47.4% 11.2%'
  },
  {
    name: '--secondary-foreground',
    displayName: 'Secondary Text',
    category: 'secondary',
    description: 'Text color for secondary elements',
    defaultLight: '222.2 47.4% 11.2%',
    defaultDark: '210 40% 98%'
  },
  
  // Accent colors
  {
    name: '--accent',
    displayName: 'Accent',
    category: 'accent',
    description: 'Accent color for highlights and interactive elements',
    defaultLight: '142.1 76.2% 36.3%',
    defaultDark: '142.1 76.2% 36.3%'
  },
  {
    name: '--accent-foreground',
    displayName: 'Accent Text',
    category: 'accent',
    description: 'Text color on accent backgrounds',
    defaultLight: '355.7 100% 97.3%',
    defaultDark: '355.7 100% 97.3%'
  },
  
  // Background colors
  {
    name: '--background',
    displayName: 'Background',
    category: 'background',
    description: 'Main page background color',
    defaultLight: '0 0% 100%',
    defaultDark: '224 71% 4%'
  },
  {
    name: '--background-paper',
    displayName: 'Card Background',
    category: 'background',
    description: 'Background color for cards and components',
    defaultLight: '0 0% 98%',
    defaultDark: '224 71% 10%'
  },
  
  // Text colors
  {
    name: '--foreground',
    displayName: 'Text',
    category: 'text',
    description: 'Primary text color',
    defaultLight: '222.2 47.4% 11.2%',
    defaultDark: '210 40% 98%'
  },
  {
    name: '--foreground-muted',
    displayName: 'Muted Text',
    category: 'text',
    description: 'Secondary/muted text color',
    defaultLight: '215.4 16.3% 46.9%',
    defaultDark: '215.4 16.3% 56.9%'
  },
  
  // Border and input colors
  {
    name: '--border',
    displayName: 'Border',
    category: 'border',
    description: 'Default border color',
    defaultLight: '214.3 31.8% 91.4%',
    defaultDark: '217.2 32.6% 17.5%'
  },
  {
    name: '--input',
    displayName: 'Input Background',
    category: 'border',
    description: 'Input field background color',
    defaultLight: '214.3 31.8% 91.4%',
    defaultDark: '217.2 32.6% 17.5%'
  },
  {
    name: '--ring',
    displayName: 'Focus Ring',
    category: 'border',
    description: 'Focus ring color for interactive elements',
    defaultLight: '215.2 77.8% 92.9%',
    defaultDark: '217.2 32.6% 17.5%'
  },
  
  // Status colors
  {
    name: '--destructive',
    displayName: 'Error/Danger',
    category: 'status',
    description: 'Color for error states and destructive actions',
    defaultLight: '0 84.2% 60.2%',
    defaultDark: '0 62.8% 30.6%'
  },
  {
    name: '--destructive-foreground',
    displayName: 'Error Text',
    category: 'status',
    description: 'Text color on error backgrounds',
    defaultLight: '210 40% 98%',
    defaultDark: '210 40% 98%'
  },
  {
    name: '--success',
    displayName: 'Success',
    category: 'status',
    description: 'Color for success states and confirmations',
    defaultLight: '142.1 76.2% 36.3%',
    defaultDark: '142.1 70.6% 40.3%'
  },
  {
    name: '--success-foreground',
    displayName: 'Success Text',
    category: 'status',
    description: 'Text color on success backgrounds',
    defaultLight: '210 40% 98%',
    defaultDark: '210 40% 98%'
  },
  {
    name: '--warning',
    displayName: 'Warning',
    category: 'status',
    description: 'Color for warning states',
    defaultLight: '47.9 95.8% 53.1%',
    defaultDark: '47.9 90.8% 48.1%'
  },
  {
    name: '--warning-foreground',
    displayName: 'Warning Text',
    category: 'status',
    description: 'Text color on warning backgrounds',
    defaultLight: '222.2 47.4% 11.2%',
    defaultDark: '222.2 47.4% 11.2%'
  },
  {
    name: '--info',
    displayName: 'Info',
    category: 'status',
    description: 'Color for informational content',
    defaultLight: '204 100% 50%',
    defaultDark: '204 90% 55%'
  },
  {
    name: '--info-foreground',
    displayName: 'Info Text',
    category: 'status',
    description: 'Text color on info backgrounds',
    defaultLight: '210 40% 98%',
    defaultDark: '210 40% 98%'
  },
  
  // Form validation colors
  {
    name: '--input-required',
    displayName: 'Required Field',
    category: 'form',
    description: 'Background color for required form fields',
    defaultLight: '48 100% 96%',
    defaultDark: '48 70% 15%'
  },
  {
    name: '--input-required-foreground',
    displayName: 'Required Field Text',
    category: 'form',
    description: 'Text color for required field indicators',
    defaultLight: '48 100% 20%',
    defaultDark: '48 100% 88%'
  },
  {
    name: '--input-error',
    displayName: 'Field Error',
    category: 'form',
    description: 'Background color for form fields with errors',
    defaultLight: '0 100% 97%',
    defaultDark: '0 50% 20%'
  },
  {
    name: '--input-error-foreground',
    displayName: 'Field Error Text',
    category: 'form',
    description: 'Text color for error indicators',
    defaultLight: '0 84% 60%',
    defaultDark: '0 100% 90%'
  },
  
  // Debug colors
  {
    name: '--debug-background',
    displayName: 'Debug Background',
    category: 'debug',
    description: 'Background color for debug overlays',
    defaultLight: '50 100% 90%',
    defaultDark: '50 100% 20%'
  },
  {
    name: '--debug-foreground',
    displayName: 'Debug Text',
    category: 'debug',
    description: 'Text color for debug information',
    defaultLight: '50 100% 20%',
    defaultDark: '50 100% 90%'
  }
];

/**
 * Get default color scheme from the predefined variables
 */
export const getDefaultColorScheme = (): {
  light: Record<string, string>;
  dark: Record<string, string>;
} => {
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};
  
  DEFAULT_COLOR_VARIABLES.forEach(variable => {
    light[variable.name] = variable.defaultLight;
    dark[variable.name] = variable.defaultDark;
  });
  
  return { light, dark };
};

/**
 * Convert HSL values to hex for color input
 */
export const hslToHex = (hsl: string): string => {
  // Parse HSL string like "222.2 47.4% 11.2%"
  const match = hsl.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!match) return '#000000';
  
  const h = parseFloat(match[1]);
  const s = parseFloat(match[2]) / 100;
  const l = parseFloat(match[3]) / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Convert hex to HSL string format used in CSS variables
 */
export const hexToHsl = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};
